import { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { openModal } from '@va/dashboard/actions/ui';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { RightArrowIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { AppliedFilters, useFiltersContext } from '@va/shared/feature-filters';
import { modalName } from '@va/standalone/shared/constants';
import { DataViewOption, ReportBlockNodes, ReportBlockProps } from '@va/types/report-block';
import { WebsiteType } from '@va/types/website';
import { DataTableV8, useControlledTableState } from '@va/ui/components/data-table';
import { ReportBlock } from '@va/ui/components/report-block';
import { ReportBlockNodeIds } from '@va/ui/components/report-block/nodes';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { OrderType } from '../../agency-white-label-v2/agency-white-label-apiClient';
import useAgencyWhiteLabelContext from '../../agency-white-label-v2/AgencyWhiteLabelContext';
import TotalCommissionPayout from './commission-header/TotalCommissionPayout';
import { useCommissionDetailsColumns } from './useCommisionDetailsColumns';

const COMMISION_DETAILS_RB = 'commission-details';

const CommissionDetailsTable = () => {
  const translate = useTranslate();
  const buildReportBlockNode = useReportBlockNodeBuilder();
  const dispatch = useDispatch();

  const nodes = useMemo<ReportBlockNodes>(
    () => ({
      rightSide: buildReportBlockNode(ReportBlockNodeIds.COMMISSION_PAYOUTS, {
        onClick: () => dispatch(openModal(modalName.COMMISSION_PAYOUTS)),
        headerComponent: (
          <TotalCommissionPayout
            icon={<RightArrowIcon className='min-h-18 min-w-18' color='#969696' />}
            onClick={() => dispatch(openModal(modalName.COMMISSION_PAYOUTS))}
          />
        ),
      }),
      filterButton: buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON),
      columnVisibility: buildReportBlockNode(ReportBlockNodeIds.COLUMN_VISIBILITY),
      appliedFilters: <AppliedFilters />,
      pagination: <div id={COMMISION_DETAILS_RB} />,
    }),
    [buildReportBlockNode, dispatch],
  );

  const visualizationComponents = useMemo<ReportBlockProps['visualizationComponents']>(
    () => ({
      [DataViewOption.table]: Table,
    }),
    [],
  );

  return (
    <ReportBlock
      id={COMMISION_DETAILS_RB}
      title={translate('standalone.accountSettings.commission.title')}
      nodes={nodes}
      visualization={{ selectedView: DataViewOption.table }}
      visualizationComponents={visualizationComponents}
    />
  );
};

const Table = () => {
  const { reportBlockId } = useFiltersContext();
  const columns = useCommissionDetailsColumns();
  const { isLoading, totalWebsitesCount, websites, websiteFilterQuery, setWebsiteFilterQuery } =
    useAgencyWhiteLabelContext();

  const { pagination, setPagination, sorting, setSorting, defaultSorting } = useControlledTableState<WebsiteType>(
    {
      pagination: { pageSize: websiteFilterQuery.length },
      defaultSorting: [{ id: 'url', desc: false }],
    },
    reportBlockId || COMMISION_DETAILS_RB,
  );

  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updater) => {
      if (typeof updater !== 'function') return;

      const updatedPagination = updater(pagination);
      setPagination(updatedPagination);
      setWebsiteFilterQuery((prevFilters) => ({
        ...prevFilters,
        start: (updatedPagination?.pageIndex ?? 0) * (updatedPagination?.pageSize ?? 0),
        length: updatedPagination?.pageSize ?? 10,
      }));
    },
    [pagination, setPagination, setWebsiteFilterQuery],
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      if (typeof updater !== 'function') return;
      const updatedSorting = updater(sorting);
      setSorting(updatedSorting);
      setWebsiteFilterQuery((prevFilters) => ({
        ...prevFilters,
        order: updatedSorting[0]?.desc ? OrderType.DESC : OrderType.ASC,
      }));
    },
    [setSorting, setWebsiteFilterQuery, sorting],
  );

  return (
    <DataTableV8<WebsiteType>
      id='commission-details-table'
      className='min-w-[62.5rem]'
      columns={columns}
      data={isLoading ? [] : websites}
      state={{ pagination, sorting }}
      paginationContainerId={COMMISION_DETAILS_RB}
      onPaginationChange={handlePaginationChange}
      onSortingChange={handleSortingChange}
      isLoading={isLoading}
      rowCount={totalWebsitesCount}
      defaultSorting={defaultSorting}
    />
  );
};

export default CommissionDetailsTable;
